import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Fuel, MapPin, RefreshCw, Users } from "lucide-react";
import { bookingType } from "@/lib/booking-revenue";
import { roomLabel } from "@/lib/rooms";
import { DOCK_LOCATION } from "@/lib/booking-approval";

import {
  availabilityByNight,
  BOAT_COUNT,
  EMPTY_NIGHT,
  ROOM_COUNT,
  type NightAvailability,
} from "@/lib/lodge-inventory";

export const Route = createFileRoute("/_authenticated/admin/calendar")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewCalendar")) {
      throw redirect({ to: "/admin/team" });
    }
  },
  head: () => ({
    meta: [
      { title: "Booking Calendar — Coffman Cove Lodging" },
      { name: "description", content: "Staff calendar of approved bookings by date, dock, and stay type." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminCalendar,
});

const DOCK_SHORT = "Coffman Cove dock";
const DAY = 86_400_000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Rooms and boats a booking holds, either for one night or the whole stay. */
type Assigned = { rooms: string[]; boats: string[] };
const NONE: Assigned = { rooms: [], boats: [] };

function assignedLabel(a: Assigned) {
  return [a.rooms.join(", "), a.boats.join(", ")].filter(Boolean).join(" · ");
}


/** A fuel / ice / damage charge staff added, tied to the night it happened. */
type Charge = {
  id: string;
  booking_id: string;
  label: string;
  amount_cents: number;
  note: string | null;
  night: string;
};

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

type Booking = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  package: string | null;
  source: string | null;
  notes: string | null;
  status: string;
};

/** Parses a YYYY-MM-DD date as a UTC day key so no timezone shifts the day. */
function dayKey(d: Date) {
  return d.toISOString().slice(0, 10);
}

function parseDay(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(t) ? t : null;
}

function prettyDay(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Every calendar day a booking occupies (arrival through the night before departure). */
function occupiedDays(b: Booking): string[] {
  const start = parseDay(b.arrival_date);
  if (start == null) return [];
  const end = parseDay(b.departure_date);
  const last = end != null && end > start ? end - DAY : start;
  const days: string[] = [];
  for (let t = start; t <= last && days.length < 90; t += DAY) {
    days.push(dayKey(new Date(t)));
  }
  return days;
}

function AdminCalendar() {
  const today = new Date();
  const [month, setMonth] = useState(() => new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)));
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const [assignments, setAssignments] = useState<Map<string, Assigned>>(new Map());
  const [charges, setCharges] = useState<Charge[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data, error }, roomRes, boatRes, chargeRes] = await Promise.all([
        supabase
          .from("booking_requests")
          .select("id,name,email,phone,arrival_date,departure_date,party_size,package,source,notes,status")
          .eq("status", "approved")
          .order("arrival_date", { ascending: true })
          .limit(500),
        supabase.from("room_assignments").select("booking_id,room_id,night").limit(5000),
        supabase.from("boat_assignments").select("booking_id,boat_name,night").limit(5000),
        supabase
          .from("booking_charges")
          .select("id,booking_id,label,amount_cents,note,charge_date,created_at")
          .limit(5000),
      ]);
      if (error) throw error;
      setBookings((data ?? []) as Booking[]);

      // Keyed by booking and by "day|booking" so a cell can show what that
      // party has that night, and the detail line can show the whole stay.
      const map = new Map<string, Assigned>();
      const put = (key: string, kind: "rooms" | "boats", value: string) => {
        const entry = map.get(key) ?? { rooms: [], boats: [] };
        if (!entry[kind].includes(value)) entry[kind].push(value);
        map.set(key, entry);
      };
      for (const r of roomRes.data ?? []) {
        const night = String(r.night).slice(0, 10);
        put(r.booking_id, "rooms", roomLabel(r.room_id));
        put(`${night}|${r.booking_id}`, "rooms", roomLabel(r.room_id));
      }
      for (const b of boatRes.data ?? []) {
        const night = String(b.night).slice(0, 10);
        put(b.booking_id, "boats", b.boat_name);
        put(`${night}|${b.booking_id}`, "boats", b.boat_name);
      }
      setAssignments(map);

      setCharges(
        (chargeRes.data ?? []).map((c: any) => ({
          id: c.id,
          booking_id: c.booking_id,
          label: c.label,
          amount_cents: c.amount_cents,
          note: c.note,
          night: String(c.charge_date ?? c.created_at).slice(0, 10),
        })),
      );
    } catch {
      toast.error("Could not load approved bookings");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the calendar in step with the inbox: the moment a request is approved,
  // or a room, boat or charge changes, the board reloads on its own.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 400);
    };

    const channel = supabase.channel("admin-calendar-sync");
    for (const table of [
      "booking_requests",
      "room_assignments",
      "boat_assignments",
      "booking_charges",
    ]) {
      channel.on("postgres_changes", { event: "*", schema: "public", table }, reload);
    }
    channel.subscribe();

    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      void supabase.removeChannel(channel);
    };
  }, [load]);

  const byDay = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const b of bookings ?? []) {
      for (const d of occupiedDays(b)) {
        const list = map.get(d) ?? [];
        list.push(b);
        map.set(d, list);
      }
    }
    return map;
  }, [bookings]);

  /** Charges keyed by night and by "night|booking" so both views can read them. */
  const chargeIndex = useMemo(() => {
    const map = new Map<string, Charge[]>();
    for (const c of charges) {
      for (const key of [c.night, `${c.night}|${c.booking_id}`]) {
        map.set(key, [...(map.get(key) ?? []), c]);
      }
    }
    return map;
  }, [charges]);

  const chargeTotal = (list: Charge[] | undefined) =>
    (list ?? []).reduce((sum, c) => sum + c.amount_cents, 0);

  const openByNight = useMemo(() => availabilityByNight(bookings ?? []), [bookings]);


  const grid = useMemo(() => {
    const first = new Date(month);
    const startOffset = first.getUTCDay();
    const gridStart = new Date(first.getTime() - startOffset * DAY);
    return Array.from({ length: 42 }, (_, i) => new Date(gridStart.getTime() + i * DAY));
  }, [month]);

  const monthLabel = month.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" });
  const todayKey = dayKey(new Date());

  const upcoming = useMemo(() => {
    return (bookings ?? [])
      .filter((b) => {
        const end = parseDay(b.departure_date) ?? parseDay(b.arrival_date);
        return end != null && end >= Date.parse(`${todayKey}T00:00:00Z`);
      })
      .slice(0, 12);
  }, [bookings, todayKey]);

  const selectedBookings = selected ? byDay.get(selected) ?? [] : [];

  function shift(months: number) {
    setMonth((m) => new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + months, 1)));
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <CalendarDays size={18} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Staff planning</span>
          </div>
          <h1 className="mt-2 font-display text-3xl">Booking calendar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Updates live — approve a request in the{" "}
            <Link to="/admin/inbox" className="underline">
              submissions inbox
            </Link>{" "}
            and it lands here straight away, with rooms, boat, party size and contact details.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} aria-hidden />
          Refresh
        </button>
      </header>

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => shift(-1)}
          aria-label="Previous month"
          className="rounded-md border border-border p-2 hover:bg-muted"
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
        <h2 className="font-display text-xl">{monthLabel}</h2>
        <button
          type="button"
          onClick={() => shift(1)}
          aria-label="Next month"
          className="rounded-md border border-border p-2 hover:bg-muted"
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-border bg-border">
        {WEEKDAYS.map((d) => (
          <div key={d} className="bg-card px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {d}
          </div>
        ))}
        {grid.map((date) => {
          const key = dayKey(date);
          const inMonth = date.getUTCMonth() === month.getUTCMonth();
          const dayBookings = byDay.get(key) ?? [];
          const open = openByNight.get(key) ?? EMPTY_NIGHT;
          const soldOut = open.roomsFree === 0 && open.boatsFree === 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key === selected ? null : key)}
              aria-label={`${prettyDay(key)} — ${open.roomsFree} of ${ROOM_COUNT} rooms open, ${open.boatsFree} of ${BOAT_COUNT} boats open`}
              className={`min-h-24 bg-card p-2 text-left align-top transition-colors hover:bg-muted/60 ${
                inMonth ? "" : "opacity-45"
              } ${key === selected ? "ring-2 ring-inset ring-[var(--color-ember)]" : ""}`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  key === todayKey
                    ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                    : "text-muted-foreground"
                }`}
              >
                {date.getUTCDate()}
              </span>
              <span className="mt-1 flex flex-wrap gap-1">
                <OpenChip label="rm" free={open.roomsFree} total={ROOM_COUNT} />
                <OpenChip label="bt" free={open.boatsFree} total={BOAT_COUNT} />
                {chargeIndex.has(key) && (
                  <span className="rounded bg-[var(--color-ember)]/15 px-1 text-[10px] font-semibold tabular-nums text-[var(--color-ember)]">
                    {money(chargeTotal(chargeIndex.get(key)))} extras
                  </span>
                )}
                {soldOut && (
                  <span className="rounded px-1 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                    Full
                  </span>
                )}
              </span>
              <span className="mt-1 block space-y-1">
                {dayBookings.slice(0, 2).map((b) => {
                  const held = assignments.get(`${key}|${b.id}`) ?? NONE;
                  const label = assignedLabel(held);
                  return (
                    <span
                      key={b.id}
                      className="block rounded bg-[var(--color-ember)]/12 px-1.5 py-0.5 text-[11px] leading-tight text-foreground"
                      title={`${b.name} · ${bookingType(b)} · ${label || "no room or boat assigned"} · ${DOCK_SHORT}`}
                    >
                      <span className="block truncate">
                        {b.name} · {bookingType(b)}
                      </span>
                      <span
                        className={`block truncate text-[10px] ${
                          label ? "text-muted-foreground" : "text-destructive"
                        }`}
                      >
                        {label || "Unassigned"}
                      </span>
                    </span>
                  );
                })}
                {dayBookings.length > 2 && (
                  <span className="block text-[10px] text-muted-foreground">+{dayBookings.length - 2} more</span>
                )}
              </span>

            </button>
          );
        })}
      </div>

      <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          <strong className="text-foreground">rm</strong> = private rooms open of {ROOM_COUNT}
        </span>
        <span>
          <strong className="text-foreground">bt</strong> = boats open of {BOAT_COUNT}
        </span>
        <span>Green = open, amber = filling up, red = none left.</span>
      </p>

      {selected && (
        <section className="mt-6 rounded-xl border border-border bg-card p-5">
          <h3 className="font-display text-lg">{prettyDay(selected)}</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <AvailabilityCard
              title="Rooms"
              total={ROOM_COUNT}
              night={openByNight.get(selected) ?? EMPTY_NIGHT}
              kind="rooms"
            />
            <AvailabilityCard
              title="Boats"
              total={BOAT_COUNT}
              night={openByNight.get(selected) ?? EMPTY_NIGHT}
              kind="boats"
            />
          </div>
          {selectedBookings.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing booked this day.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {selectedBookings.map((b) => (
                <li key={b.id} className="rounded-lg border border-border p-3">
                  <BookingLine booking={b} assigned={assignments.get(`${selected}|${b.id}`) ?? NONE} />
                  <NightCharges list={chargeIndex.get(`${selected}|${b.id}`) ?? []} />

                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-display text-xl">Upcoming approved bookings</h2>
        {bookings === null ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : upcoming.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No approved bookings on the books yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {upcoming.map((b) => (
              <li key={b.id} className="p-4">
                <BookingLine booking={b} assigned={assignments.get(b.id) ?? NONE} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Fuel and other extras a party ran up on one night. */
function NightCharges({ list }: { list: Charge[] }) {
  if (list.length === 0) {
    return <p className="mt-2 text-xs text-muted-foreground">No fuel or extra charges this night.</p>;
  }
  const total = list.reduce((sum, c) => sum + c.amount_cents, 0);
  return (
    <div className="mt-3 rounded-md bg-muted/40 p-2">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Fuel size={12} aria-hidden /> Charges this night
      </p>
      <ul className="mt-1 space-y-0.5 text-sm">
        {list.map((c) => (
          <li key={c.id} className="flex justify-between gap-3">
            <span>
              {c.label}
              {c.note ? <span className="text-xs text-muted-foreground"> — {c.note}</span> : null}
            </span>
            <span className="tabular-nums">{money(c.amount_cents)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 flex justify-between border-t border-border/60 pt-1 text-sm font-semibold">
        <span>Night total</span>
        <span className="tabular-nums">{money(total)}</span>
      </p>
    </div>
  );
}

function BookingLine({ booking, assigned = NONE }: { booking: Booking; assigned?: Assigned }) {
  const arrival = booking.arrival_date ? prettyDay(booking.arrival_date.slice(0, 10)) : "Dates TBD";
  const departure = booking.departure_date ? prettyDay(booking.departure_date.slice(0, 10)) : null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold">{booking.name}</span>
        <span className="rounded-full bg-[var(--color-ember)]/12 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-ember)]">
          {bookingType(booking)}
        </span>
        {assigned.rooms.map((r) => (
          <span key={r} className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold">
            {r}
          </span>
        ))}
        {assigned.boats.map((b) => (
          <span key={b} className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold">
            {b}
          </span>
        ))}
        {assigned.rooms.length === 0 && assigned.boats.length === 0 && (
          <span className="rounded-full border border-destructive/40 px-2 py-0.5 text-[11px] font-semibold text-destructive">
            No room or boat assigned
          </span>
        )}
      </div>

      <p className="mt-1 text-sm text-muted-foreground">
        {arrival}
        {departure ? ` → ${departure}` : ""}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin size={12} aria-hidden /> {DOCK_SHORT}
        </span>
        {booking.party_size ? (
          <span className="inline-flex items-center gap-1">
            <Users size={12} aria-hidden /> {booking.party_size} anglers
          </span>
        ) : null}
        {booking.email ? (
          <a href={`mailto:${booking.email}`} className="underline">
            {booking.email}
          </a>
        ) : null}
        {booking.phone ? (
          <a href={`tel:${booking.phone.replace(/[^\d+]/g, "")}`} className="underline">
            {booking.phone}
          </a>
        ) : null}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">{DOCK_LOCATION}</p>
    </div>
  );
}

/** Colour by how much is left: green plenty, amber tight, red none. */
function openTone(free: number, total: number) {
  if (free <= 0) return "bg-destructive/15 text-destructive";
  if (free <= Math.max(1, Math.floor(total / 3))) return "bg-amber-500/15 text-amber-600 dark:text-amber-400";
  return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
}

function OpenChip({ label, free, total }: { label: string; free: number; total: number }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${openTone(free, total)}`}>
      {free}/{total} {label}
    </span>
  );
}

function AvailabilityCard({
  title,
  total,
  night,
  kind,
}: {
  title: string;
  total: number;
  night: NightAvailability;
  kind: "rooms" | "boats";
}) {
  const free = kind === "rooms" ? night.roomsFree : night.boatsFree;
  const booked = kind === "rooms" ? night.roomsBooked : night.boatsBooked;
  const pct = Math.min(100, Math.round((Math.min(booked, total) / total) * 100));
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold">{title}</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold tabular-nums ${openTone(free, total)}`}>
          {free} open
        </span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-[var(--color-ember)]" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground tabular-nums">
        {booked} of {total} booked
        {booked > total ? " — overbooked" : ""}
      </p>
    </div>
  );
}
